import { Button, Stack } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";

import AddPointsDialog from "./AddPointsDialog";
import DeductPointsDialog from "./DeductPointsDialog";
import {
  SearchRequest,
  useGetActivityTypes,
  useGetCustomerInfo,
  useGetInsuranceInfo,
  useGetInsuranceTransaction,
  useGetRedemptionHistory,
  useGetRewardStoreType,
} from "../../../api/customer";
import Text from "../../../components/atoms/Text";
import CustomerInfoDataGrid from "../../../components/customer360/customerInfoDataGrid/CustomerInfoDataGrid";
import InsuranceInformationDataGrid from "../../../components/customer360/insuranceInformation/InsuranceInformationDataGrid";
import InsuranceTransactionDataGrid from "../../../components/customer360/InsuranceTransaction/InsuranceTransactionDataGrid";
import RedemptionHistoryDataGrid from "../../../components/customer360/redemptionHistoryDataGrid/RedemptionHistoryDataGrid";
import DetailPageHeader from "../../../components/organisms/createProductHeader/DetailPageHeader";
import { usePermissionStore } from "../../../stores/permissionStore";

const CustomerDetail = () => {
  const { customerId } = useParams<{
    customerId: string;
  }>();

  const requestDefault = {
    omneId: customerId,
    pagination: {
      currentPage: 1,
      pageSize: 10,
    },
    permission_Token: usePermissionStore.getState().permissionToken ?? "",
  };
  const [requestRedemptionHistory, setRequestRedemptionHistory] =
    useState<SearchRequest>(requestDefault);
  const [requestInsurance, setRequestInsurance] =
    useState<SearchRequest>(requestDefault);
  const [requestInsuranceTransaction, setRequestInsuranceTransaction] =
    useState<SearchRequest>(requestDefault);

  const { data: dataCustomerInfo, isFetching: isFetchingCustomerInfo } =
    useGetCustomerInfo(customerId ?? "");
  const {
    data: dataRedemptionHistory,
    isFetching: isFetchingRedemptionHistory,
  } = useGetRedemptionHistory(requestRedemptionHistory);
  const { data: dataActivityTypes } = useGetActivityTypes({
    permission_Token: usePermissionStore.getState().permissionToken ?? "",
  });
  const { data: dataRewardStoreType } = useGetRewardStoreType({
    permission_Token: usePermissionStore.getState().permissionToken ?? "",
  });
  const { data: dataInsuranceInfo, isFetching: isFetchingInsurance } =
    useGetInsuranceInfo(requestInsurance);
  const {
    data: dataInsuranceTransaction,
    isFetching: isFetchingInsuranceTransaction,
  } = useGetInsuranceTransaction(requestInsuranceTransaction);

  const [isShowAddPointsDialog, setIsShowAddPointsDialog] = useState(false);
  const [isShowDeductPointsDialog, setIsShowDeductPointsDialog] =
    useState(false);

  const { permissions } = usePermissionStore();
  const withAjustPointsAuth =
    permissions &&
    Object.keys(permissions[0]?.allowedActions).includes("adjustPoints");

  const isShowInsuranceTransaction =
    permissions &&
    Object.keys(permissions[0]?.allowedActions).includes(
      "insuranceTransaction",
    );

  const isShowCustomerInfo =
    permissions &&
    Object.keys(permissions[0]?.allowedActions).includes("customerInfo");

  const isShowInsuranceInfo =
    permissions &&
    Object.keys(permissions[0]?.allowedActions).includes("insuranceInfo");

  const isShowRedemptionHistory =
    permissions &&
    Object.keys(permissions[0]?.allowedActions).includes("redemptionHistory");

  return (
    <>
      <DetailPageHeader isProduct={false} />
      <Stack padding={4}>
        {isShowCustomerInfo && (
          <>
            <Text variant="title_small" color="#E87722" mb={1.5}>
              Omne customer Information
            </Text>
            {/* TODO: update design */}
            <CustomerInfoDataGrid
              data={dataCustomerInfo && [dataCustomerInfo?.data]}
              loading={isFetchingCustomerInfo}
            />
          </>
        )}
        {isShowInsuranceInfo && (
          <>
            <Text variant="title_small" color="#E87722" mt={7} mb={1.5}>
              Insurance Information
            </Text>
            <InsuranceInformationDataGrid
              onPageChange={page => {
                setRequestInsurance({
                  ...requestInsurance,
                  pagination: {
                    ...requestInsurance.pagination,
                    currentPage: page,
                  },
                });
              }}
              data={dataInsuranceInfo?.data}
              loading={isFetchingInsurance}
            />
          </>
        )}
        {isShowInsuranceTransaction && (
          <>
            <Text variant="title_small" color="#E87722" mt={6} mb={1.5}>
              Insurance Transaction Information
            </Text>
            <InsuranceTransactionDataGrid
              onPageChange={page => {
                setRequestInsuranceTransaction({
                  ...requestInsuranceTransaction,
                  pagination: {
                    ...requestInsuranceTransaction.pagination,
                    currentPage: page,
                  },
                });
              }}
              data={dataInsuranceTransaction?.data}
              loading={isFetchingInsuranceTransaction}
              signupCountry={dataCustomerInfo?.data?.signupCountry || ""}
            />
          </>
        )}
        {isShowRedemptionHistory && (
          <>
            <Stack
              direction="row"
              spacing={4}
              style={{ alignItems: "flex-end" }}
            >
              <Text variant="title_small" color="#E87722" mt={7} mb={1.5}>
                Redemption History
              </Text>
              {withAjustPointsAuth && (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => setIsShowAddPointsDialog(true)}
                    style={{ marginBottom: 8, borderRadius: 6 }}
                  >
                    Add Points
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setIsShowDeductPointsDialog(true)}
                    style={{ marginBottom: 8, borderRadius: 6 }}
                  >
                    Deduct Points
                  </Button>
                </>
              )}
            </Stack>
            <RedemptionHistoryDataGrid
              onPageChange={page => {
                setRequestRedemptionHistory({
                  ...requestRedemptionHistory,
                  pagination: {
                    ...requestRedemptionHistory.pagination,
                    currentPage: page,
                  },
                });
              }}
              data={dataRedemptionHistory?.data}
              loading={isFetchingRedemptionHistory}
            />
          </>
        )}
      </Stack>
      <AddPointsDialog
        dataActivityTypes={dataActivityTypes}
        isShow={isShowAddPointsDialog}
        omneId={dataCustomerInfo?.data?.omneId}
        mode={"ADD"}
        handleClose={() => setIsShowAddPointsDialog(false)}
      />
      <DeductPointsDialog
        dataRewardStoreType={dataRewardStoreType}
        isShow={isShowDeductPointsDialog}
        omneId={dataCustomerInfo?.data?.omneId}
        mode={"DEDUCT"}
        handleClose={() => setIsShowDeductPointsDialog(false)}
      />
    </>
  );
};

export default CustomerDetail;
